import React, { useState } from "react";
import ProductPageLayout from "@/components/ProductPageLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { motion } from "framer-motion";
import rewardsHeroImage from "@/assets/rewards-hero.webp";
import giftCardImage from "@/assets/gift-card-catalog.webp";
import amazonImage from "@/assets/amazon-products.webp";
import customRewardsImage from "@/assets/custom-rewards.webp";
import companySwagImage from "@/assets/company-swag.webp";
import { ShoppingCart, Plane, Shirt, Gift, Star, TrendingUp, Globe, CheckCircle, Heart, Target, Sparkles, Play, ArrowRight, ChevronDown } from "lucide-react";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import TrustLogosSection from "@/components/trust/TrustLogosSection";
import HeroBadgesSection from "@/components/hero/HeroBadgesSection";
const Rewards = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const rewardTypes = [{
    icon: <ShoppingCart className="h-6 w-6" />,
    title: "Amazon Business",
    description: "Browse millions of products from Amazon Business catalog",
    features: ["US & Canada stores", "Business Prime eligible", "Credit card + points", "Automated ordering"],
    color: "from-orange-500 to-amber-500"
  }, {
    icon: <Gift className="h-6 w-6" />,
    title: "Gift Cards",
    description: "Global gift card catalog with 100+ countries supported",
    features: ["100+ countries", "Local currency support", "Popular brands", "Instant delivery"],
    color: "from-pink-500 to-rose-500"
  }, {
    icon: <Heart className="h-6 w-6" />,
    title: "Charitable Donations",
    description: "Make meaningful impact with donations to verified charities",
    features: ["Verified nonprofits", "Global causes", "Impact tracking", "Tax receipts"],
    color: "from-emerald-500 to-teal-500"
  }, {
    icon: <Plane className="h-6 w-6" />,
    title: "Travel & Experiences",
    description: "Redeem points for flights, hotels, and activities worldwide",
    features: ["Switchfly integration", "Global experiences", "Flexible payments", "Easy cancellations"],
    color: "from-blue-500 to-cyan-500"
  }, {
    icon: <Shirt className="h-6 w-6" />,
    title: "Company Swag",
    description: "Custom branded merchandise through multiple providers",
    features: ["Axomo & Covver", "Custom catalogs", "International shipping", "Inventory management"],
    color: "from-purple-500 to-pink-500"
  }, {
    icon: <Star className="h-6 w-6" />,
    title: "Member Discounts",
    description: "Access thousands of discounted products and services",
    features: ["MemberDeals.com", "Instant access", "No points required", "International options"],
    color: "from-green-500 to-teal-500"
  }];
  const benefits = [{
    icon: <TrendingUp className="h-6 w-6" />,
    title: "89% Increased Engagement",
    description: "Teams report higher satisfaction when rewards match personal preferences"
  }, {
    icon: <Heart className="h-6 w-6" />,
    title: "94% Employee Satisfaction",
    description: "Workers feel more valued with access to meaningful reward choices"
  }, {
    icon: <Target className="h-6 w-6" />,
    title: "67% Better ROI",
    description: "Companies see improved recognition program effectiveness and participation"
  }];
  return <ProductPageLayout title="Employee Rewards Platform - Turn Recognition into Real Rewards" description="Assembly's rewards platform offers flexible rewards your employees actually want. From Amazon products to gift cards, travel experiences, and custom swag." keywords="employee rewards platform, amazon business rewards, travel rewards, company swag, recognition software, employee incentives, points redemption" ctaTitle="Want to Learn More?" ctaDescription="See how Assembly's rewards platform can transform your employee recognition program." ctaButtonText="Get a Demo" ctaVariant="blue" onCtaClick={() => setIsDemoModalOpen(true)}>
      {/* Full Height Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-50 via-gray-50 to-coral-50/30 font-sans overflow-hidden min-h-screen">
        {/* Subtle Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-coral-500/8 via-orange-500/5 to-coral-400/4 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] bg-gradient-to-br from-orange-400/6 via-coral-500/5 to-slate-400/3 rounded-full blur-3xl animate-pulse" style={{
          animationDelay: '1s'
        }}></div>
        </div>
        
        {/* Dot Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.15]">
          <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(251, 113, 133, 0.3) 2px, transparent 0)`,
          backgroundSize: '48px 48px'
        }}></div>
        </div>

        <section className="relative flex items-center justify-center py-8 lg:py-10 px-4 lg:px-8 min-h-screen pt-20 lg:pt-24">
          <div className="container mx-auto px-4 2xl:px-12 max-w-none lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1600px] relative px-6 lg:px-6 xl:px-8 pb-3 lg:pb-4">
            <div className="flex flex-col lg:grid lg:grid-cols-7 gap-6 lg:gap-4 items-start">
              {/* Left Section: Text Content */}
              <div className="flex flex-col items-start text-left lg:col-span-4 h-full justify-center space-y-4 lg:space-y-6">
                {/* Pills */}
                <motion.div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3 mt-6 lg:mt-8 mb-3 lg:mb-4" initial={{
                opacity: 0,
                y: -20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.7
              }}>
                  <div className="relative overflow-hidden px-3 py-1.5 rounded-full bg-black/5 border border-black/10 backdrop-blur-sm hover:bg-black/10 transition-all duration-300 hover:-translate-y-0.5">
                    <h1 className="text-xs font-normal text-gray-900 tracking-wide uppercase">The Complete Way to Reward Your People</h1>
                  </div>
                </motion.div>

                {/* Main Heading */}
                <div className="mb-4 lg:mb-6">
                  <motion.h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-5xl xl:text-6xl font-bold text-gray-900 tracking-tight font-sans" initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.8,
                  delay: 0.2
                }}>
                    <span className="block mb-0 sm:mb-0 md:mb-0 lg:mb-4">Reward your employees</span>
                    <span className="block">
                      <span className="mr-2">and customers -</span>
                      <span className="relative inline-block font-extrabold">
                        instantly
                        <span className="absolute inset-x-0 bottom-1 h-3 bg-gradient-to-r from-coral-200/60 via-coral-300/70 to-coral-200/60 rounded-sm transform scale-x-0 origin-left transition-transform duration-1000 ease-out -z-10" style={{
                        transform: 'scaleX(1)',
                        transitionDelay: '200ms'
                      }}></span>
                      </span>
                    </span>
                  </motion.h2>
                </div>

                {/* Subheading */}
                <motion.p className="text-sm sm:text-base lg:text-base xl:text-lg text-gray-600 mb-6 lg:mb-8 leading-relaxed" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.8,
                delay: 0.4
              }}>
                  Assembly Rewards makes it simple to recognize great work and celebrate every moment that matters.
                  Send rewards your people actually love — from gift cards and swag to experiences and company perks — all from one platform.
                </motion.p>
                
                {/* Action Buttons */}
                <motion.div className="mb-4 lg:mb-6 w-full flex flex-col sm:flex-row gap-3" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.8,
                delay: 0.6
              }}>
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md transition-all duration-200" 
                    onClick={() => setIsDemoModalOpen(true)}
                  >
                    Book a Demo
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="w-full sm:w-auto bg-white border-2 border-gray-300 text-gray-900 hover:bg-gray-100 hover:text-gray-900 hover:border-gray-400 shadow-sm transition-all duration-200" 
                    asChild
                  >
                    <a href="https://app.joinassembly.com/signup" target="_blank" rel="noopener noreferrer">
                      Start for Free
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </motion.div>
                
                {/* Trust Ratings */}
                <motion.div className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-start sm:items-center w-full" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.8,
                delay: 0.8
              }}>
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400 fill-yellow-400" />
                    <span className="text-sm font-semibold text-gray-700">4.9 / 5 on G2</span>
                    <img src="/lovable-uploads/e68d106f-69a9-45e8-8624-7f09b1ae1e6f.png" alt="G2 Logo" className="h-4 w-4 sm:h-5 sm:w-5" width={20} height={20} />
                  </div>
                  <a href="https://www.g2.com/products/assembly-2021-12-09/reviews" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                    <span className="text-sm font-semibold text-gray-500">3000+ reviews</span>
                  </a>
                </motion.div>
              </div>
              
              {/* Right Section: Hero Image */}
              <div className="relative w-full flex items-center justify-center lg:col-span-3 mt-6 lg:mt-0">
                <div className="relative group">
                  <motion.img src={rewardsHeroImage} alt="Assembly Rewards Platform showing recognition to reward delivery flow - office, remote, and field employees receiving gift cards, experiences, and perks" className="w-full h-auto object-cover rounded-xl transition-transform duration-300 group-hover:scale-105" loading="eager" decoding="async" fetchPriority="high" initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.8,
                  delay: 0.4
                }} />
                </div>
              </div>
            </div>
            
            {/* Trust Section at Bottom */}
            <motion.div className="text-center pt-8 lg:pt-12 mt-8 space-y-8" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 1.0
          }}>
              <p className="text-lg text-gray-600">
                The #1 Employee Recognition & Rewards Platform - Trusted by Leading Teams Worldwide
              </p>
              <HeroBadgesSection />
              <div className="pt-4">
                <TrustLogosSection />
              </div>
            </motion.div>
          </div>
        </section>
      </div>

      {/* Find the Perfect Gift */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }}>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Find the perfect gift with flexible rewards
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                With over 100+ countries supported and thousands of gift card options, your team can choose rewards that matter to them. Local currencies, regional brands, and instant delivery worldwide.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-coral-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Global Gift Card Catalog</h3>
                    <p className="text-gray-600">100+ countries with local currency support</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-coral-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Popular Brands</h3>
                    <p className="text-gray-600">Amazon, Starbucks, Target, and more</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-coral-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Instant Delivery</h3>
                    <p className="text-gray-600">Digital rewards delivered immediately</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative">
              <img src={giftCardImage} alt="Gift Cards Catalog showing popular brands like Target, Amazon, Nordstrom, and more" className="w-full rounded-3xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Amazon Integration */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="order-2 lg:order-1">
              <img src={amazonImage} alt="Amazon Business Integration showing product catalog with electronics and essentials" className="w-full rounded-3xl" />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <ShoppingCart className="h-4 w-4" />
                Amazon Business
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Redeem points directly on Amazon
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Browse millions of products from the Amazon Business catalog. Your team can use points alone or combine them with a credit card for higher-value purchases.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-coral-500 rounded-full"></div>
                  <span className="text-gray-700">US & Canada store connections</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-coral-500 rounded-full"></div>
                  <span className="text-gray-700">Business Prime eligible products</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-coral-500 rounded-full"></div>
                  <span className="text-gray-700">Flexible payment: points + credit card</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Custom Rewards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }}>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Create custom rewards specific to your company
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Build a unique rewards catalog tailored to your organization. Add company-specific perks, experiences, or benefits that align with your culture and values.
              </p>
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">Popular Custom Rewards</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Extra PTO Day</span>
                    <span className="text-coral-500 font-semibold">500 points</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Premium Parking Spot</span>
                    <span className="text-coral-500 font-semibold">300 points</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Team Lunch with CEO</span>
                    <span className="text-coral-500 font-semibold">1,000 points</span>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }}>
              <img src={customRewardsImage} alt="Custom Rewards catalog showing PTO days, parking spots, bring your dog to work, and more" className="w-full rounded-3xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Company Swag */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="order-2 lg:order-1">
              <img src={companySwagImage} alt="Company branded swag including mugs, water bottles, notebooks, and pens" className="w-full rounded-3xl" />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Shirt className="h-4 w-4" />
                Company Swag
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Celebrate your team with custom swag
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Build your own branded swag store with custom merchandise. From t-shirts to water bottles, let your team show their company pride with quality products they'll actually use.
              </p>
              <div className="flex flex-wrap gap-3">
                <Badge className="bg-white text-purple-700 border-purple-200 px-4 py-2 text-sm">Axomo and Covver Integration</Badge>
                <Badge className="bg-white text-purple-700 border-purple-200 px-4 py-2 text-sm">Custom Catalogs</Badge>
                <Badge className="bg-white text-purple-700 border-purple-200 px-4 py-2 text-sm">Global Shipping</Badge>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Simple Points System */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">Earning points is simple - redeeming them is even easier</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              A straightforward rewards system that your employees will love to use
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <motion.div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0
          }}>
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Earn Points</h3>
              <p className="text-gray-600">Receive recognition and earn points through peer-to-peer shoutouts, milestones, and achievements</p>
            </motion.div>

            <motion.div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }}>
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingCart className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Browse Rewards</h3>
              <p className="text-gray-600">Explore our extensive catalog and find the perfect reward from thousands of options</p>
            </motion.div>

            <motion.div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }}>
              <div className="w-16 h-16 bg-coral-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Redeem Instantly</h3>
              <p className="text-gray-600">Get your reward delivered instantly with no waiting periods or approval processes</p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Heart className="h-5 w-5 text-coral-500" />
                For Employees
              </h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Points balance always visible</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Easy-to-use rewards marketplace</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Order tracking and history</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Target className="h-5 w-5 text-coral-500" />
                For Administrators
              </h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Budget controls and limits</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Real-time spending analytics</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Automated fulfillment</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-20 bg-gradient-to-br from-coral-50 to-rose-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              Rewards that keep teams engaged and motivated
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              See the measurable impact of a comprehensive rewards platform
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => <motion.div key={index} className="bg-white rounded-2xl p-8 text-center shadow-lg" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }}>
                <div className="text-5xl font-bold text-coral-500 mb-3">
                  {benefit.title.split(' ')[0]}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {benefit.title.split(' ').slice(1).join(' ')}
                </h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* All Reward Options Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              Every reward option in one platform
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Give your team access to the most comprehensive rewards catalog available
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rewardTypes.map((reward, index) => <motion.div key={index} className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-coral-200 transition-all hover:shadow-lg" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }}>
                <div className={`w-14 h-14 bg-gradient-to-r ${reward.color} rounded-xl flex items-center justify-center mb-4 text-white`}>
                  {reward.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{reward.title}</h3>
                <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                  {reward.description}
                </p>
                <div className="space-y-2">
                  {reward.features.slice(0, 2).map((feature, fIndex) => <div key={fIndex} className="flex items-center gap-2 text-xs text-gray-700">
                      <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>)}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about Assembly Rewards.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <AccordionTrigger className="px-6 py-5 hover:bg-gray-50">
                <span className="text-left font-semibold text-gray-900">
                  Can I reward employees who aren't active on Assembly?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-gray-600 leading-relaxed">
                  Yes. You can send rewards to anyone — even if they don't log in.
                  They'll receive a secure link to redeem their reward by email or message, perfect for deskless or hybrid employees.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <AccordionTrigger className="px-6 py-5 hover:bg-gray-50">
                <span className="text-left font-semibold text-gray-900">
                  How do points and direct rewards work together?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-gray-600 leading-relaxed">
                  Assembly Rewards supports both points-based and instant rewards, all managed in one place.
                  You can automate recognition through points or send one-time rewards instantly — everything is tracked in the same dashboard.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <AccordionTrigger className="px-6 py-5 hover:bg-gray-50">
                <span className="text-left font-semibold text-gray-900">
                  What kind of rewards can we offer?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-gray-600 leading-relaxed">
                  Choose from thousands of options including gift cards, swag, experiences, company culture perks, and donations.
                  You can also add custom rewards unique to your organization.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <AccordionTrigger className="px-6 py-5 hover:bg-gray-50">
                <span className="text-left font-semibold text-gray-900">
                  How do budgets and payments work?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-gray-600 leading-relaxed">
                  You can pre-fund your account or connect a company payment method.
                  Budget controls and tax handling are built in, giving admins full visibility and flexibility.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <AccordionTrigger className="px-6 py-5 hover:bg-gray-50">
                <span className="text-left font-semibold text-gray-900">
                  Is Assembly Rewards available globally?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-gray-600 leading-relaxed">
                  Yes. The platform supports multiple currencies and local catalogs, so employees everywhere can redeem rewards that feel local and relevant.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <AccordionTrigger className="px-6 py-5 hover:bg-gray-50">
                <span className="text-left font-semibold text-gray-900">
                  Can we integrate with our HR or communication tools?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-gray-600 leading-relaxed">
                  Absolutely. Assembly connects with Slack, Microsoft Teams, BambooHR, Rippling, Gusto, Covver, Switchfly, and more — so rewarding happens where your team already works.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <AccordionTrigger className="px-6 py-5 hover:bg-gray-50">
                <span className="text-left font-semibold text-gray-900">
                  How fast do employees receive their rewards?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-gray-600 leading-relaxed">
                  Instantly. Once a reward is sent, employees receive it via email, chat, or link — with digital delivery handled automatically.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      <DemoBookingModal open={isDemoModalOpen} onOpenChange={setIsDemoModalOpen} />
    </ProductPageLayout>;
};
export default Rewards;